import React from "react";
import { Box, Divider } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  Offer,
  OfferServiceRequest,
  WarrantyPeriodCode,
  OfferPaymentTermsCode,
  ValidityPeriodCode,
} from "../../models/models";
import { PublicServiceRequest } from "../../../serviceBoard/models/models";
import { dt } from "../../../../shared/themes/redesignTokens";
import { getUiLocale } from "../../../../shared/utils/relativeTime";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

interface StepReviewSendProps {
  offer?: Partial<Offer>;
  serviceRequest?: OfferServiceRequest;
  fullServiceRequest?: PublicServiceRequest;
}

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return "\u2014";
  return new Intl.NumberFormat(getUiLocale(), {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const DetailRow = ({ label, value }: { label: string; value: string | React.ReactNode }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: 0.75 }}>
    <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textSecondary, flex: "0 0 40%" }}>
      {label}
    </Box>
    <Box sx={{ fontFamily: dt.fontMono, fontSize: "13px", fontWeight: 500, color: dt.textPrimary, flex: "0 0 60%", textAlign: "right" }}>
      {value}
    </Box>
  </Box>
);

const IncludedServiceRow = ({ label, included }: { label: string; included: boolean }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
    {included ? (
      <CheckIcon sx={{ color: "#10B981", fontSize: 16 }} />
    ) : (
      <CloseIcon sx={{ color: dt.textMuted, fontSize: 16 }} />
    )}
    <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: included ? dt.textPrimary : dt.textMuted }}>
      {label}
    </Box>
  </Box>
);

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box
    sx={{
      background: dt.cardBg,
      borderRadius: dt.radius,
      border: `1px solid ${dt.cardBorder}`,
      boxShadow: dt.cardShadow,
      p: 2.5,
    }}
  >
    <Box sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 600, color: dt.textSecondary, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5 }}>
      {title}
    </Box>
    {children}
  </Box>
);

const StepReviewSend = ({ offer, serviceRequest, fullServiceRequest }: StepReviewSendProps) => {
  const { translate } = useTranslation(["serviceOffers"]);

  if (!offer) return null;

  const calculateTotalCost = () =>
    (offer.laborCost || 0) + (offer.partsCost || 0) + (offer.travelCost || 0) + (offer.additionalCosts || 0);

  const getWarrantyLabel = () => {
    const map: Record<number, string> = {
      [WarrantyPeriodCode.thirtyDays]: translate("warrantyPeriodCodes.thirtyDays"),
      [WarrantyPeriodCode.ninetyDays]: translate("warrantyPeriodCodes.ninetyDays"),
      [WarrantyPeriodCode.sixMonths]: translate("warrantyPeriodCodes.sixMonths"),
      [WarrantyPeriodCode.oneYear]: translate("warrantyPeriodCodes.oneYear"),
      [WarrantyPeriodCode.custom]: offer.customWarrantyPeriod || "\u2014",
    };
    return offer.warrantyPeriodCode ? map[offer.warrantyPeriodCode] || "\u2014" : "\u2014";
  };

  const getPaymentLabel = () => {
    const map: Record<number, string> = {
      [OfferPaymentTermsCode.paymentOnCompletion]: translate("paymentTermsCodes.paymentOnCompletion"),
      [OfferPaymentTermsCode.net30Days]: translate("paymentTermsCodes.net30Days"),
      [OfferPaymentTermsCode.fiftyAdvanceFiftyCompletion]: translate("paymentTermsCodes.fiftyAdvanceFiftyCompletion"),
      [OfferPaymentTermsCode.net60Days]: translate("paymentTermsCodes.net60Days"),
      [OfferPaymentTermsCode.custom]: offer.customPaymentTerms || "\u2014",
    };
    return offer.paymentTermsCode ? map[offer.paymentTermsCode] || "\u2014" : "\u2014";
  };

  const getValidityLabel = () => {
    const map: Record<number, string> = {
      [ValidityPeriodCode.threeDays]: translate("validityPeriodCodes.threeDays"),
      [ValidityPeriodCode.fiveDays]: translate("validityPeriodCodes.fiveDays"),
      [ValidityPeriodCode.sevenDays]: translate("validityPeriodCodes.sevenDays"),
      [ValidityPeriodCode.fourteenDays]: translate("validityPeriodCodes.fourteenDays"),
      [ValidityPeriodCode.thirtyDays]: translate("validityPeriodCodes.thirtyDays"),
      [ValidityPeriodCode.custom]: offer.customValidityPeriod || "\u2014",
    };
    return offer.validityPeriodCode ? map[offer.validityPeriodCode] || "\u2014" : "\u2014";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Section title */}
      <Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "20px", fontWeight: 700, color: dt.textPrimary, mb: 0.5 }}>
          {translate("reviewTitle")}
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textMuted }}>
          {translate("reviewHelperText")}
        </Box>
      </Box>

      {/* Pricing breakdown */}
      <SectionCard title={translate("pricingDetails")}>
        <DetailRow label={translate("laborCost")} value={formatCurrency(offer.laborCost)} />
        {offer.laborHours != null && (
          <DetailRow label={translate("laborHoursInput")} value={`${offer.laborHours} hrs`} />
        )}
        {offer.hourlyRate != null && (
          <DetailRow label={translate("hourlyRateInput")} value={formatCurrency(offer.hourlyRate)} />
        )}
        <DetailRow label={translate("partsCost")} value={formatCurrency(offer.partsCost)} />
        {offer.materialDescription && (
          <DetailRow label={translate("materialDescriptionLabel")} value={offer.materialDescription} />
        )}
        <DetailRow label={translate("travelCost")} value={formatCurrency(offer.travelCost)} />
        {offer.travelDistanceKm != null && (
          <DetailRow label={translate("distanceKm")} value={`${offer.travelDistanceKm} km`} />
        )}
        {offer.travelTimeHours != null && (
          <DetailRow label={translate("travelTimeHours")} value={`${offer.travelTimeHours} hrs`} />
        )}
        {offer.travelRatePerHour != null && (
          <DetailRow label={translate("travelRatePerHour")} value={formatCurrency(offer.travelRatePerHour)} />
        )}
        <DetailRow label={translate("additionalCosts")} value={formatCurrency(offer.additionalCosts)} />
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
          <Box sx={{ fontFamily: dt.font, fontSize: "15px", fontWeight: 700, color: dt.textPrimary }}>
            {translate("totalEstimatedCost")}
          </Box>
          <Box sx={{ fontFamily: dt.fontMono, fontSize: "18px", fontWeight: 700, color: dt.blue }}>
            {formatCurrency(calculateTotalCost())}
          </Box>
        </Box>
      </SectionCard>

      {/* Scheduling */}
      <SectionCard title={translate("schedulingTitle")}>
        {offer.immediateStart ? (
          <DetailRow label={translate("immediateStartLabel")} value={translate("common:yes")} />
        ) : (
          <DetailRow
            label={translate("earliestStartDate")}
            value={offer.earliestStartDate ? formatUiDate(offer.earliestStartDate) : "\u2014"}
          />
        )}
        {offer.availableDates && (
          <DetailRow
            label={translate("availableDatesLabel")}
            value={
              (() => {
                try {
                  const dates = JSON.parse(offer.availableDates);
                  return Array.isArray(dates)
                    ? dates.map((d: string) => formatUiDate(d, "dayMonth")).join(", ")
                    : "\u2014";
                } catch {
                  return "\u2014";
                }
              })()
            }
          />
        )}
      </SectionCard>

      {/* Service Notes */}
      {offer.serviceApproach && (
        <SectionCard title={translate("serviceNotesLabel")}>
          <Box sx={{ fontFamily: dt.font, fontSize: "14px", color: dt.textPrimary, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
            {offer.serviceApproach}
          </Box>
        </SectionCard>
      )}

      {/* Included Services */}
      <SectionCard title={translate("includedServices")}>
        <IncludedServiceRow label={translate("includeDiagnostics")} included={offer.includeDiagnostics || false} />
        <IncludedServiceRow label={translate("includeLaborForRepair")} included={offer.includeLaborForRepair || false} />
        <IncludedServiceRow label={translate("includePartsAndMaterials")} included={offer.includePartsAndMaterials || false} />
        <IncludedServiceRow label={translate("includeTestingAndCalibration")} included={offer.includeTestingAndCalibration || false} />
        <IncludedServiceRow label={translate("includeWorkAreaCleanup")} included={offer.includeWorkAreaCleanup || false} />
        <IncludedServiceRow label={translate("includeDetailedServiceReport")} included={offer.includeDetailedServiceReport || false} />
      </SectionCard>

      {/* Terms */}
      <SectionCard title={translate("termsAndConditions")}>
        <DetailRow label={translate("warrantyPeriod")} value={getWarrantyLabel()} />
        <DetailRow label={translate("paymentTerms")} value={getPaymentLabel()} />
        <DetailRow label={translate("offerValidityPeriod")} value={getValidityLabel()} />
      </SectionCard>

    </Box>
  );
};

export default StepReviewSend;
